import { useCallback, useEffect, useState } from 'react';
import type { WorkflowDefinition } from '@corner/shared';
import {
  createUserWorkflow,
  deleteUserWorkflow,
  getUserWorkflow,
  listUserWorkflows,
  updateUserWorkflow,
  type UserWorkflowDTO,
} from '../api/workflowApi';

export interface UseUserWorkflowsOptions {
  isSignedIn: boolean;
}

export function useUserWorkflows({ isSignedIn }: UseUserWorkflowsOptions) {
  const [list, setList] = useState<UserWorkflowDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isSignedIn) { setList([]); setError(null); return; }
    setLoading(true);
    setError(null);
    try {
      const workflows = await listUserWorkflows();
      setList(workflows);
    } catch (err) {
      setList([]);
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(
    async (input?: { forkPresetId?: string; name?: string; description?: string }) => {
      const workflow = await createUserWorkflow(input);
      await refresh();
      return workflow;
    },
    [refresh],
  );

  const fetchOne = useCallback(
    async (id: string) => getUserWorkflow(id),
    [],
  );

  const update = useCallback(
    async (
      id: string,
      patch: {
        name?: string;
        description?: string;
        status?: 'draft' | 'published';
        definition?: WorkflowDefinition;
      },
    ) => {
      const workflow = await updateUserWorkflow(id, patch);
      await refresh();
      return workflow;
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteUserWorkflow(id);
      await refresh();
    },
    [refresh],
  );

  return { list, loading, error, refresh, create, fetchOne, update, remove };
}
